import React, { useState, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

const Tooltip = ({ 
  content, 
  children, 
  position = 'bottom',
  showIcon = true,
  iconClassName = "ml-1 h-4 w-4 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
  tooltipClassName = "w-80 md:w-96 whitespace-normal break-words text-left leading-6 text-sm"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  // Hide tooltip on resize/scroll to avoid awkward placements
  useEffect(() => {
    const hide = () => setIsVisible(false);
    window.addEventListener('resize', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('resize', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  const calculatePosition = (element) => {
    const rect = element.getBoundingClientRect();
    const tooltipWidth = 320; // Approximate width (w-80)
    const tooltipHeight = 60; // Approximate height
    
    let top, left;
    
    switch (position) {
      case 'top':
        top = rect.top - tooltipHeight - 8;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.left - tooltipWidth - 8;
        break;
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.right + 8;
        break;
      default:
        top = rect.bottom + 8;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    }
    
    // Ensure tooltip stays within viewport
    const maxLeft = window.innerWidth - tooltipWidth - 16;
    const maxTop = window.innerHeight - tooltipHeight - 16;
    
    left = Math.max(16, Math.min(left, maxLeft));
    top = Math.max(16, Math.min(top, maxTop));
    
    return { top, left };
  };

  const showTooltip = (event) => {
    const pos = calculatePosition(event.currentTarget);
    setTooltipPosition(pos);
    setIsVisible(true);
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900'
  };

  if (showIcon) {
    return (
      <div className="relative inline-flex items-center">
        {children}
        <div
          className="relative rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60"
          onMouseEnter={showTooltip}
          onMouseLeave={() => setIsVisible(false)}
          onFocus={showTooltip}
          onBlur={() => setIsVisible(false)}
          onClick={showTooltip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showTooltip(e); }
          }}
          onTouchStart={(e) => { e.preventDefault(); showTooltip(e); }}
          role="button"
          tabIndex={0}
          aria-label="Help"
          aria-expanded={isVisible}
        >
          <HelpCircle className={iconClassName} />
        </div>
        {isVisible && (
          <div 
            className="fixed z-[9999] pointer-events-none"
            style={{ 
              top: `${tooltipPosition.top}px`, 
              left: `${tooltipPosition.left}px` 
            }}
          >
            <div className={`
              rounded-lg bg-gray-900 px-3.5 py-2.5 text-white shadow-float ring-1 ring-white/10 dark:bg-gray-700
              ${tooltipClassName}
            `}>
              {content}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className="relative inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={showTooltip}
        onBlur={() => setIsVisible(false)}
        onClick={showTooltip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showTooltip(e); }
        }}
        onTouchStart={(e) => { e.preventDefault(); showTooltip(e); }}
        role="group"
        tabIndex={0}
        aria-expanded={isVisible}
      >
        {children}
      </div>
      {isVisible && (
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{ 
            top: `${tooltipPosition.top}px`, 
            left: `${tooltipPosition.left}px` 
          }}
        >
          <div className={`
            rounded-lg bg-gray-900 px-3.5 py-2.5 text-white shadow-float ring-1 ring-white/10 dark:bg-gray-700
            ${tooltipClassName}
          `}>
            {content}
          </div>
        </div>
      )}
    </>
  );
};

export default Tooltip;
